import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Send, Users, MessageCircle, X, UserMinus, Trash2, ArrowLeft, Image as ImageIcon, Film, UserPlus, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';
import { showNotification } from '../utils/notifications';
import LevelBadge, { ExpProgressBar } from '../components/LevelBadge';
import { compressPostImage } from '../utils/imageCompression';

const Messages = ({ user, onBack, onUpdateUser, searchUsername, clearSearchUsername }) => {
  // Load cached chats immediately for instant display
  const loadCachedChats = () => {
    try {
      const cachedChats = localStorage.getItem(`chats_${user.id}`);
      if (cachedChats) {
        return JSON.parse(cachedChats);
      }
    } catch (e) {
      console.error('Error loading cached chats:', e);
    }
    return { direct_chats: [], group_chats: [] };
  };

  const initialChats = loadCachedChats();
  const hasCache = initialChats.direct_chats.length > 0 || initialChats.group_chats.length > 0;

  const [chats, setChats] = useState(initialChats);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(!hasCache); // Only show loading if no cache
  const [messageInput, setMessageInput] = useState('');
  const [ws, setWs] = useState(null);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showNewGroupModal, setShowNewGroupModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const [showFriendsModal, setShowFriendsModal] = useState(false);
  const [friends, setFriends] = useState([]);
  const [friendSearchQuery, setFriendSearchQuery] = useState('');
  const [friendSearchResults, setFriendSearchResults] = useState([]);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const messagesEndRef = useRef(null);
  const videoCameraRef = useRef(null);
  const messageInputRef = useRef(null);
  const [showChatListBgModal, setShowChatListBgModal] = useState(false);
  const [showMessageAreaBgModal, setShowMessageAreaBgModal] = useState(false);
  const [uploadingChatListBg, setUploadingChatListBg] = useState(false);
  const [uploadingMessageAreaBg, setUploadingMessageAreaBg] = useState(false);
  const [notification, setNotification] = useState(null);
  const notificationAudioRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const [showVideoOptions, setShowVideoOptions] = useState(false);
  const [selectedMessageForAction, setSelectedMessageForAction] = useState(null);
  const [showMessageMenu, setShowMessageMenu] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editContent, setEditContent] = useState('');
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const longPressTimer = useRef(null);

  // Initialize WebSocket
  useEffect(() => {
    const wsUrl = `${API.replace('https://', 'wss://').replace('http://', 'ws://').replace('/api', '')}/ws/${user.id}`;
    const websocket = new WebSocket(wsUrl);

    websocket.onopen = () => {
      console.log('✓ WebSocket connected');
    };

    websocket.onmessage = async (event) => {
      const data = JSON.parse(event.data);
      
      // Handle incoming message
      if (data.chat_id && data.sender_id) {
        // Check if message is from someone else (not current user)
        const isFromOtherUser = data.sender_id !== user.id;
        
        // Show notification if message is from someone else
        console.log('📨 Messages WebSocket received message from other user:', isFromOtherUser);
        console.log('User notifications_enabled:', user.notifications_enabled);
        console.log('Document hidden (app in background):', document.hidden);
        
        if (isFromOtherUser) {
          // ALWAYS show native PWA notification for messages from others
          // This works in both foreground and background
          console.log('🔔 Messages: Calling showNotification...');
          try {
            await showNotification('Beypointer - New Message', {
              sender: data.sender_username || 'New Message',
              message: data.content || '📷 Photo/Video',
              chatId: data.chat_id
            });
            console.log('✅ Messages: showNotification completed');
          } catch (error) {
            console.error('❌ Messages: showNotification failed:', error);
          }
          
          // Show in-app popup notification only if notifications enabled
          if (user.notifications_enabled !== false) {
            setNotification({
              sender: data.sender_username || 'New Message',
              message: data.content || '📷 Photo/Video',
              chatId: data.chat_id
            });
            
            // Auto-hide notification after 5 seconds
            setTimeout(() => {
              setNotification(null);
            }, 5000);
          }
        } else {
          console.log('ℹ️ Messages: Message from self, no notification needed');
        }
        
        // Update app badge for PWA (works even when on Messages page)
        // CRITICAL: This ensures badge updates regardless of which page user is on when backgrounded
        if (isFromOtherUser) {
          console.log('🔔 Messages: Updating app badge...');
          if ('setAppBadge' in navigator) {
            // Fetch accurate unread count from server
            // Backend already incremented unread_counts before broadcasting message
            try {
              const token = localStorage.getItem('token');
              const response = await axios.get(`${API}/chat/list`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              const unreadCount = response.data.total_unread || 0;
              
              if (unreadCount > 0) {
                await navigator.setAppBadge(unreadCount);
                console.log(`✅ App badge updated to ${unreadCount} (from Messages page)`);
              } else {
                // Clear badge if no unread messages
                await navigator.clearAppBadge();
                console.log('✅ App badge cleared (no unread messages)');
              }
            } catch (error) {
              console.error('❌ Error updating app badge:', error);
            }
          } else {
            console.log('⚠️ setAppBadge not supported in this browser');
          }
        }
        
        // Add message to state if viewing this chat
        if (activeChat && data.chat_id === activeChat.id) {
          setMessages(prev => {
            // Prevent duplicate messages
            if (prev.some(msg => msg.id === data.id)) {
              return prev;
            }
            const newMessages = [...prev, data];
            
            console.log('✅ New message added to active chat:', data.content);
            
            // Update cache with new message
            try {
              localStorage.setItem(`messages_${data.chat_id}`, JSON.stringify(newMessages));
            } catch (e) {
              console.error('Error updating message cache:', e);
            }
            
            return newMessages;
          });
          
          // Scroll to bottom for new messages
          scrollToBottom();
        } else {
          // Message for a different chat - just update cache
          try {
            const cachedMessages = localStorage.getItem(`messages_${data.chat_id}`);
            const messages = cachedMessages ? JSON.parse(cachedMessages) : [];
            if (!messages.some(msg => msg.id === data.id)) {
              messages.push(data);
              localStorage.setItem(`messages_${data.chat_id}`, JSON.stringify(messages));
              console.log('✅ Message cached for chat:', data.chat_id);
            }
          } catch (e) {
            console.error('Error caching message:', e);
          }
        }
        
        // Update chat list silently in background
        fetchChats(true);
        
        // Only mark as read if viewing this chat AND app is in foreground
        // This ensures notifications appear when app is backgrounded
        if (activeChat?.id === data.chat_id && data.sender_id !== user.id && !document.hidden) {
          try {
            const token = localStorage.getItem('token');
            await axios.post(
              `${API}/chat/${data.chat_id}/mark-read`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            console.log('✓ Marked message as read (app in foreground)');
            
            // Update badge after marking as read
            const response = await axios.get(`${API}/chat/list`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const unreadCount = response.data.total_unread || 0;
            
            if ('setAppBadge' in navigator) {
              if (unreadCount > 0) {
                await navigator.setAppBadge(unreadCount);
                console.log(`✅ Badge updated to ${unreadCount} after marking as read`);
              } else {
                await navigator.clearAppBadge();
                console.log('✅ Badge cleared (all messages read)');
              }
            }
          } catch (error) {
            console.error('Error marking message as read:', error);
          }
        } else if (activeChat?.id === data.chat_id && data.sender_id !== user.id && document.hidden) {
          console.log('📱 App in background - NOT marking as read to allow notifications');
        }
      }
      
      // Handle message edit
      if (data.type === 'message_edit') {
        setMessages(prev => prev.map(msg => 
          msg.id === data.message_id 
            ? { ...msg, content: data.content, edited: true }
            : msg
        ));
        
        // Update cache
        if (activeChat && data.chat_id === activeChat.id) {
          try {
            const cachedMessages = localStorage.getItem(`messages_${data.chat_id}`);
            if (cachedMessages) {
              const messages = JSON.parse(cachedMessages);
              const updatedMessages = messages.map(msg => 
                msg.id === data.message_id 
                  ? { ...msg, content: data.content, edited: true }
                  : msg
              );
              localStorage.setItem(`messages_${data.chat_id}`, JSON.stringify(updatedMessages));
            }
          } catch (e) {
            console.error('Error updating message cache:', e);
          }
        }
      }
      
      // Handle message delete
      if (data.type === 'message_delete') {
        setMessages(prev => prev.filter(msg => msg.id !== data.message_id));
        
        // Update cache
        if (activeChat && data.chat_id === activeChat.id) {
          try {
            const cachedMessages = localStorage.getItem(`messages_${data.chat_id}`);
            if (cachedMessages) {
              const messages = JSON.parse(cachedMessages);
              const updatedMessages = messages.filter(msg => msg.id !== data.message_id);
              localStorage.setItem(`messages_${data.chat_id}`, JSON.stringify(updatedMessages));
            }
          } catch (e) {
            console.error('Error updating message cache:', e);
          }
        }
      }
      
      // Handle group closed notification
      if (data.type === 'group_closed') {
        toast.info(data.message);
        if (activeChat?.id === data.group_id) {
          setActiveChat(null);
        }
        fetchChats(true); // Silent update in background
      }
    };

    websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    websocket.onclose = () => {
      console.log('WebSocket disconnected');
    };

    setWs(websocket);

    return () => {
      if (websocket) {
        websocket.close();
      }
    };
  }, [user.id]);

  // Fetch friends list on component mount
  useEffect(() => {
    console.log('Messages component mounted, fetching friends...');
    fetchFriends();
    
    // Chats are already loaded from cache in initial state
    // Just fetch fresh data in background to update
    console.log('📡 Fetching fresh chats in background...');
    fetchChats(true);
  }, []);

  // Handle app coming back from background - mark active chat as read
  useEffect(() => {
    const handleVisibilityChange = async () => {
      // When app comes back to foreground and user has an active chat open
      if (!document.hidden && activeChat) {
        console.log('📱 App returned to foreground with active chat, marking as read...');
        try {
          const token = localStorage.getItem('token');
          await axios.post(
            `${API}/chat/${activeChat.id}/mark-read`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log('✓ Marked active chat as read after returning to foreground');
          
          // Update badge after marking as read
          const response = await axios.get(`${API}/chat/list`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const unreadCount = response.data.total_unread || 0;
          
          if ('setAppBadge' in navigator) {
            if (unreadCount > 0) {
              await navigator.setAppBadge(unreadCount);
              console.log(`✅ Badge updated to ${unreadCount} after visibility change`);
            } else {
              await navigator.clearAppBadge();
              console.log('✅ Badge cleared after visibility change');
            }
          }
        } catch (error) {
          console.error('Error marking as read on visibility change:', error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [activeChat]);

  // Fetch user chats
  const fetchChats = async (silent = false) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/chat/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update with fresh data
      setChats(response.data);
      setChatsLoading(false);
      
      // Cache chats to localStorage for instant loading next time
      try {
        localStorage.setItem(`chats_${user.id}`, JSON.stringify(response.data));
        console.log('💾 Chats cached for next time');
      } catch (e) {
        console.error('Error caching chats:', e);
      }
    } catch (error) {
      setChatsLoading(false);
      if (!silent) {
        console.error('Error fetching chats:', error);
      }
    }
  };

  // Load messages for a chat
  const loadChat = async (chat) => {
    setActiveChat(chat);
    
    // Load cached messages immediately for instant display
    try {
      const cachedMessages = localStorage.getItem(`messages_${chat.id}`);
      if (cachedMessages) {
        setMessages(JSON.parse(cachedMessages));
        setTimeout(scrollToBottom, 100);
        console.log('✓ Loaded cached messages for chat:', chat.id);
      }
    } catch (e) {
      console.error('Error loading cached messages:', e);
    }
    
    // Fetch fresh messages and mark as read in parallel (only if app in foreground)
    try {
      const token = localStorage.getItem('token');
      
      // Parallel requests for faster loading
      const requests = [
        axios.get(`${API}/chat/${chat.id}/messages`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ];
      
      // Only mark as read if app is in foreground (not backgrounded)
      if (!document.hidden) {
        requests.push(
          axios.post(
            `${API}/chat/${chat.id}/mark-read`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          ).catch(err => console.log('Mark read failed:', err))
        );
        console.log('✓ Will mark messages as read (app in foreground)');
      } else {
        console.log('📱 App in background - NOT marking as read');
      }
      
      const [messagesResponse] = await Promise.all(requests);
      
      setMessages(messagesResponse.data.messages);
      setTimeout(scrollToBottom, 100);
      
      // Update badge after opening chat and marking as read
      if (!document.hidden) {
        try {
          const chatListResponse = await axios.get(`${API}/chat/list`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const unreadCount = chatListResponse.data.total_unread || 0;
          
          if ('setAppBadge' in navigator) {
            if (unreadCount > 0) {
              await navigator.setAppBadge(unreadCount);
              console.log(`✅ Badge updated to ${unreadCount} after opening chat`);
            } else {
              await navigator.clearAppBadge();
              console.log('✅ Badge cleared after opening chat');
            }
          }
        } catch (error) {
          console.error('Error updating badge:', error);
        }
      }
      
      // Cache messages for instant loading next time
      try {
        localStorage.setItem(`messages_${chat.id}`, JSON.stringify(messagesResponse.data.messages));
      } catch (e) {
        console.error('Error caching messages:', e);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  // Send message
  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (5MB limit before compression)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large (max 5MB)');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    try {
      // Compress image before sending (max 200KB)
      const compressedFile = await compressPostImage(file);
      
      // Read and convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result);
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      console.error('Error compressing image:', error);
      // Fall back to original file
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result);
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (50MB limit before conversion)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video too large (max 50MB)');
      return;
    }

    // Check file type
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }

    // Read and convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedVideo(reader.result);
      setVideoPreview(URL.createObjectURL(file));
    };
    reader.readAsDataURL(file);
  };

  const clearVideo = () => {
    setSelectedVideo(null);
    setVideoPreview(null);
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const sendMessage = async () => {
    if ((!messageInput.trim() && !selectedImage && !selectedVideo) || !activeChat) {
      console.log('⚠️ Cannot send: missing content or activeChat', {
        hasInput: !!messageInput.trim(),
        hasImage: !!selectedImage,
        hasVideo: !!selectedVideo,
        hasActiveChat: !!activeChat,
        activeChatId: activeChat?.id
      });
      return;
    }

    const messageContent = messageInput.trim();
    const imageData = selectedImage;
    const videoData = selectedVideo;
    
    setMessageInput(''); // Clear input immediately for better UX
    clearImage(); // Clear selected image
    clearVideo(); // Clear selected video
    
    if (videoData) {
      setIsConverting(true);
      toast.info('Converting video to 1080p...');
    }

    try {
      const token = localStorage.getItem('token');
      console.log('📤 Sending message to chat:', activeChat.id);
      console.log('📝 Message content:', messageContent);
      console.log('🔑 Token available:', !!token);
      
      const response = await axios.post(
        `${API}/chat/${activeChat.id}/send`,
        { 
          content: messageContent || "",
          image: imageData || null,
          video: videoData || null
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000 // 2 minutes for video conversion
        }
      );
      
      if (videoData) {
        setIsConverting(false);
        toast.success('Video converted and sent!');
      }
      
      console.log('Message sent successfully:', response.data);
      
      // Immediately add the message to local state
      const newMessage = {
        id: response.data.message_id,
        chat_id: activeChat.id,
        sender_id: user.id,
        sender_username: user.username,
        content: messageContent || "",
        image: imageData,
        video: response.data.video || videoData, // Use converted video from server
        timestamp: new Date().toISOString(),
        is_system_message: false
      };
      
      setMessages(prev => {
        const newMessages = [...prev, newMessage];
        
        // Update cache with sent message
        try {
          localStorage.setItem(`messages_${activeChat.id}`, JSON.stringify(newMessages));
        } catch (e) {
          console.error('Error updating message cache:', e);
        }
        
        return newMessages;
      });
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('❌ Error sending message:', error);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error message:', error.message);
      console.error('❌ Chat ID:', activeChat?.id);
      console.error('❌ Token exists:', !!localStorage.getItem('token'));
      
      setIsConverting(false);
      
      // Show detailed error to user
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to send message';
      toast.error(`Failed to send message: ${errorMsg}`);
      
      // Restore message input on error
      setMessageInput(messageContent);
    }
  };

  // Edit message
  const handleEditMessage = async (messageId, newContent) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/chat/${activeChat.id}/message/${messageId}`,
        { content: newContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Message edited');
      setEditingMessage(null);
      setEditContent('');
    } catch (error) {
      console.error('Error editing message:', error);
      toast.error(error.response?.data?.detail || 'Failed to edit message');
    }
  };

  // Delete message
  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message for everyone?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/chat/${activeChat.id}/message/${messageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Message deleted');
      setShowMessageMenu(false);
      setSelectedMessageForAction(null);
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  };

  // Long press handlers
  const handleLongPressStart = (msg, e) => {
    if (msg.sender_id !== user.id || msg.is_system_message) return;
    
    // Prevent text selection and context menu
    e.preventDefault();
    
    longPressTimer.current = setTimeout(() => {
      setSelectedMessageForAction(msg);
      setShowMessageMenu(true);
    }, 500); // 500ms long press
  };

  const handleLongPressEnd = (e) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  };

  // Search users
  const searchUsers = async (query, groupMode = false) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/search?q=${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let filtered = response.data.filter(u => u.username !== user.username);
      
      // For group chat, only show mutual friends
      if (groupMode) {
        // Get current user's friends
        const friendsResponse = await axios.get(`${API}/friends/list`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const friendIds = friendsResponse.data.friends.map(f => f.id);
        
        // Filter to only mutual friends (users who also have current user as friend)
        filtered = filtered.filter(u => {
          // Check if user is in current user's friends list
          return friendIds.includes(u.id);
        });
      }
      
      setSearchResults(filtered);
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  // Create direct chat
  const createDirectChat = async (otherUserId, otherUsername) => {
    try {
      const token = localStorage.getItem('token');
      console.log('Creating chat with user:', otherUserId, otherUsername);
      
      const response = await axios.post(
        `${API}/chat/direct/create?other_user_id=${otherUserId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Chat created:', response.data);
      
      // Close modal and clear search
      setShowNewChatModal(false);
      setSearchQuery('');
      setSearchResults([]);
      
      // Fetch full participant profile data
      const otherUserResponse = await axios.get(`${API}/users/search?q=${otherUsername}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const otherUserProfile = otherUserResponse.data.find(u => u.username === otherUsername) || {};
      
      // Immediately load the chat for messaging with full profile data
      const newChat = {
        id: response.data.chat_id,
        participants: [user.id, otherUserId],
        participant_usernames: [user.username, otherUsername],
        participant_profiles: {
          [otherUserId]: {
            username: otherUsername,
            profile_picture: otherUserProfile.profile_picture,
            banner_image: otherUserProfile.banner_image,
            circle_color: otherUserProfile.circle_color,
            battle_points: otherUserProfile.battle_points || 0
          }
        }
      };
      
      console.log('Active chat set to:', newChat);
      setActiveChat(newChat);
      setMessages([]);
      
      // Refresh chat list in background
      fetchChats();
      
      if (response.data.exists) {
        toast.success('Opening existing chat...');
      } else {
        toast.success('Chat ready! Start messaging...');
      }
    } catch (error) {
      console.error('Error creating chat:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.status === 403) {
        toast.info('This user has disabled messages');
      } else {
        toast.error('Failed to create chat');
      }
    }
  };

  // Create group chat
  const createGroupChat = async () => {
    if (!groupName.trim() || selectedMembers.length === 0) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/chat/group/create`,
        { 
          name: groupName,
          member_ids: selectedMembers.map(m => m.id)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowNewGroupModal(false);
      setGroupName('');
      setSelectedMembers([]);
      setSearchQuery('');
      setSearchResults([]);
      await fetchChats();
      toast.success('Group created!');
    } catch (error) {
      console.error('Error creating group:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to create group');
      }
    }
  };

  // Fetch friends list
  const fetchFriends = async () => {
    try {
      console.log('Fetching friends list...');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/friends/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Friends API response:', response.data);
      console.log('Number of friends:', response.data.friends?.length || 0);
      
      if (response.data && response.data.friends) {
        setFriends(response.data.friends);
        console.log('Friends state updated with:', response.data.friends.length, 'friends');
      } else {
        console.log('No friends data in response');
        setFriends([]);
      }
    } catch (error) {
      console.error('Error fetching friends:', error);
      console.error('Error details:', error.response?.data);
      setFriends([]);
    }
  };

  // Search for users to add as friends
  const searchForFriends = async (query) => {
    if (!query || query.length < 2) {
      setFriendSearchResults([]);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/search?q=${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // The endpoint returns an array directly, not wrapped in an object
      const users = Array.isArray(response.data) ? response.data : [];
      // Filter out already friends and current user
      const filtered = users.filter(
        u => u.username !== user.username && !friends.find(f => f.id === u.id)
      );
      setFriendSearchResults(filtered);
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  // Add friend
  const addFriend = async (friendId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/friends/add/${friendId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Friend added');
      fetchFriends();
      setFriendSearchQuery('');
      setFriendSearchResults([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add friend');
    }
  };

  // Remove friend
  const removeFriend = async (friendId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/friends/remove/${friendId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Friend removed');
      fetchFriends();
    } catch (error) {
      toast.error('Failed to remove friend');
    }
  };

  // Fetch group members details
  const fetchGroupMembers = async () => {
    if (!activeChat || !isGroupChat(activeChat)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/chat/group/${activeChat.id}/members`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGroupMembers(response.data.members);
      setShowMembersModal(true);
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Failed to load members');
    }
  };

  // Kick member from group
  const kickMember = async (memberId) => {
    if (!activeChat) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/chat/group/${activeChat.id}/kick`,
        { member_id: memberId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Member removed');
      // Refresh members list
      fetchGroupMembers();
      fetchChats();
      // Refresh active chat
      const chatsResponse = await axios.get(`${API}/chat/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allChats = [...chatsResponse.data.direct_chats, ...chatsResponse.data.group_chats];
      const updatedChat = allChats.find(c => c.id === activeChat.id);
      if (updatedChat) {
        setActiveChat(updatedChat);
      }
    } catch (error) {
      console.error('Error kicking member:', error);
      toast.error('Failed to remove member');
    }
  };

  // Close group
  const closeGroup = async () => {
    if (!activeChat) return;

    // Confirmation dialog
    const confirmed = window.confirm('Are you sure you want to close this group? This action cannot be undone.');
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/chat/group/${activeChat.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveChat(null);
      toast.success('Group closed');
      fetchChats();
    } catch (error) {
      // Error hidden - silent fail
    }
  };

  // Clear direct chat
  const clearDirectChat = async () => {
    if (!activeChat || isGroupChat(activeChat)) return;

    // Confirmation dialog
    const confirmed = window.confirm('Are you sure you want to clear this chat? All messages will be deleted and this action cannot be undone.');
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/chat/direct/${activeChat.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveChat(null);
      toast.success('Chat cleared');
      fetchChats();
    } catch (error) {
      // Error hidden - silent fail
    }
  };

  const handleChatListBgUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingChatListBg(true);

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        const token = localStorage.getItem('token');
        await axios.post(
          `${API}/profile/upload-chat-list-background`,
          { profile_picture: base64Image },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        toast.success('Chat list background updated!');
        await onUpdateUser();
        setShowChatListBgModal(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading chat list background:', error);
      toast.error('Failed to update chat list background');
    } finally {
      setUploadingChatListBg(false);
    }
  };

  const handleRemoveChatListBg = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/profile/chat-list-background`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Chat list background removed!');
      await onUpdateUser();
      setShowChatListBgModal(false);
    } catch (error) {
      console.error('Error removing chat list background:', error);
    }
  };

  const handleMessageAreaBgUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingMessageAreaBg(true);

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        const token = localStorage.getItem('token');
        await axios.post(
          `${API}/profile/upload-message-area-background`,
          { profile_picture: base64Image },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        toast.success('Message area background updated!');
        await onUpdateUser();
        setShowMessageAreaBgModal(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading message area background:', error);
      toast.error('Failed to update message area background');
    } finally {
      setUploadingMessageAreaBg(false);
    }
  };

  const handleRemoveMessageAreaBg = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/profile/message-area-background`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message area background removed!');
      await onUpdateUser();
      setShowMessageAreaBgModal(false);
    } catch (error) {
      console.error('Error removing message area background:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Close video options when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showVideoOptions && !e.target.closest('.video-options-container')) {
        setShowVideoOptions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showVideoOptions]);

  useEffect(() => {
    // Load cached chats immediately for instant display
    try {
      const cachedChats = localStorage.getItem(`chats_${user.id}`);
      if (cachedChats) {
        setChats(JSON.parse(cachedChats));
        console.log('✓ Loaded cached chats');
      }
    } catch (e) {
      console.error('Error loading cached chats:', e);
    }
    
    // Fetch fresh chats in background
    fetchChats();
    
    // Set up periodic refresh (every 5 seconds)
    const refreshInterval = setInterval(() => {
      fetchChats(true); // Silent refresh in background
    }, 5000);
    
    return () => clearInterval(refreshInterval);
  }, []);

  // Poll for new messages when viewing a chat (backup for WebSocket)
  useEffect(() => {
    if (!activeChat) return;

    const pollMessages = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/chat/${activeChat.id}/messages`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Update messages if we have new ones
        setMessages(prevMessages => {
          const newMessages = response.data.messages;
          // Only update if message count changed
          if (newMessages.length !== prevMessages.length) {
            console.log('📥 Polled and found new messages');
            // Update cache
            try {
              localStorage.setItem(`messages_${activeChat.id}`, JSON.stringify(newMessages));
            } catch (e) {
              console.error('Error caching polled messages:', e);
            }
            return newMessages;
          }
          return prevMessages;
        });
      } catch (error) {
        console.error('Error polling messages:', error);
      }
    };

    // Poll every 3 seconds for new messages (backup mechanism)
    const pollInterval = setInterval(pollMessages, 3000);

    return () => clearInterval(pollInterval);
  }, [activeChat?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-search and open chat when searchUsername is provided (from Map)
  useEffect(() => {
    if (searchUsername) {
      console.log('[Messages] Auto-searching for user:', searchUsername);
      
      // Perform search
      const autoSearch = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/users/search?q=${searchUsername}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const filtered = response.data.filter(u => u.username !== user.username);
          
          if (filtered.length > 0) {
            // Find exact match
            const exactMatch = filtered.find(u => u.username.toLowerCase() === searchUsername.toLowerCase());
            if (exactMatch) {
              console.log('[Messages] Found exact match, opening chat');
              // Clear search UI and close modal
              setSearchQuery('');
              setSearchResults([]);
              setShowNewChatModal(false);
              
              // Open chat using the same method as clicking search results
              await createDirectChat(exactMatch.id || exactMatch.username, exactMatch.username);
            } else {
              // Show search results if no exact match
              setSearchQuery(searchUsername);
              setSearchResults(filtered);
            }
          } else {
            // No results found
            setSearchQuery(searchUsername);
            setSearchResults([]);
            toast.error(`User "${searchUsername}" not found`);
          }
        } catch (error) {
          console.error('[Messages] Error auto-searching:', error);
          toast.error('Failed to search for user');
        }
      };
      
      autoSearch();
      
      // Clear the search username after handling
      if (clearSearchUsername) {
        clearSearchUsername();
      }
    }
  }, [searchUsername]);

  // Get chat display name
  const getChatName = (chat) => {
    if (chat.name) return chat.name; // Group chat
    // Direct chat - show other user's name
    return chat.participant_usernames.find(name => name !== user.username) || 'Unknown';
  };

  const isGroupChat = (chat) => {
    return chat && chat.members && Array.isArray(chat.members);
  };
  
  const formatTimestamp = (timestamp) => {
    if (!timestamp) {
      console.log('No timestamp provided');
      return '';
    }
    
    // Parse the ISO string or Date object
    // Backend sends UTC time, JavaScript will convert to local time
    const msgDate = new Date(timestamp);
    
    // Check if date is valid
    if (isNaN(msgDate.getTime())) {
      console.error('Invalid timestamp:', timestamp);
      return '';
    }
    
    const now = new Date();
    
    // Use local time for comparison
    const msgLocalDate = new Date(msgDate.getFullYear(), msgDate.getMonth(), msgDate.getDate());
    const nowLocalDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Calculate day difference in local timezone
    const daysDiff = Math.floor((nowLocalDate - msgLocalDate) / 86400000);
    
    // Format time as HH:MM AM/PM in local timezone - ALWAYS show actual time
    const timeStr = msgDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    // If today (same local day) - show time only
    if (daysDiff === 0) {
      return timeStr;
    }
    
    // If yesterday
    if (daysDiff === 1) {
      return `Yesterday ${timeStr}`;
    }
    
    // If within a week
    if (daysDiff < 7) {
      const dayName = msgDate.toLocaleDateString('en-US', { weekday: 'short' });
      return `${dayName} ${timeStr}`;
    }
    
    // Older messages
    return msgDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex flex-col overflow-hidden" style={{ touchAction: 'none' }}>
      {/* Fixed Header */}
      <div className="flex-shrink-0 px-0 py-0" style={{ paddingTop: 'calc(env(safe-area-inset-top))', touchAction: 'auto' }}>
        <div className="max-w-7xl mx-auto px-2">
          {/* Header */}
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-3">
            <button
              onClick={() => {
                console.log('Back button clicked. Active chat:', activeChat);
                // If viewing a chat, go back to chat list
                // Otherwise, go back to dashboard
                if (activeChat) {
                  console.log('Active chat exists, closing chat view');
                  setActiveChat(null);
                } else {
                  console.log('No active chat, going back to dashboard');
                  onBack();
                }
              }}
              className="text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-sm font-bold text-cyan-400 digital-font">MESSAGES</h1>
            </div>
            <div className="flex gap-1">
            <Button
              onClick={() => setShowNewChatModal(true)}
              size="sm"
              className="bg-cyan-500 hover:bg-cyan-600 text-xs px-2"
            >
              <MessageCircle className="w-3 h-3 mr-1" />
              Chat
            </Button>
            <Button
              onClick={() => setShowNewGroupModal(true)}
              size="sm"
              className="bg-cyan-500 hover:bg-cyan-600 text-xs px-2"
            >
              <Users className="w-3 h-3 mr-1" />
              Group
            </Button>
            <Button
              onClick={() => setShowFriendsModal(true)}
              size="sm"
              className="bg-purple-500 hover:bg-purple-600 text-xs px-2"
            >
              <UserPlus className="w-3 h-3 mr-1" />
              Friends
            </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 px-0 pb-0 overflow-hidden" style={{ touchAction: 'auto' }}>
        <div className="max-w-7xl mx-auto h-full min-h-0 overflow-hidden">
          <div className="grid md:grid-cols-3 gap-0 h-full min-h-0 overflow-hidden">
          {/* Chat List */}
          <div 
            className={`bg-slate-800/50 rounded-t-lg border-t border-slate-700 overflow-y-auto relative min-h-0 ${activeChat ? 'hidden md:block' : ''}`}
            style={user.chat_list_background ? {
              backgroundImage: `url(${user.chat_list_background})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              touchAction: 'pan-y'
            } : { touchAction: 'pan-y' }}
          >
            {/* Overlay for better visibility */}
            {user.chat_list_background && (
              <div className="absolute inset-0 bg-black/40 rounded-t-lg"></div>
            )}

            <div className="p-4 relative z-10">
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-semibold ${user.chat_list_background ? 'text-white drop-shadow-lg' : 'text-white'}`}>Chats</h3>
                <button
                  onClick={() => setShowChatListBgModal(true)}
                  className="text-cyan-400 hover:text-cyan-300 text-xs transition-colors"
                  title="Customize background"
                >
                  Customize
                </button>
              </div>
              
              {/* Direct Chats */}
              {chats.direct_chats.map((chat) => {
                const unreadCount = chat.unread_counts?.[user.id] || 0;
                // Get other user's ID and profile
                const otherUserId = chat.participants?.find(id => id !== user.id);
                const otherUserProfile = chat.participant_profiles?.[otherUserId] || {};
                const otherUserProfilePic = otherUserProfile.profile_picture;
                const otherUserBanner = otherUserProfile.banner_image;
                const otherUserCircleColor = otherUserProfile.circle_color;
                const otherUserBattlePoints = otherUserProfile.battle_points || 0;
                
                return (
                  <button
                    key={chat.id}
                    onClick={() => loadChat(chat)}
                    className={`w-full text-left p-3 rounded-lg mb-2 transition-colors relative ${
                      activeChat?.id === chat.id
                        ? 'bg-cyan-500/30'
                        : 'bg-slate-700/50 hover:bg-slate-700'
                    }`}
                    style={otherUserBanner ? {
                      backgroundImage: `url(${otherUserBanner})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat'
                    } : {}}
                  >
                    {/* Overlay for better text visibility when banner is set */}
                    {otherUserBanner && (
                      <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                    )}
                    
                    {/* Level Badge */}
                    <LevelBadge level={otherUserProfile.level || 1} small={true} verified={otherUserProfile.has_made_first_purchase} />
                    
                    <div className="flex items-center justify-between gap-3 relative z-10">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="relative">
                          {otherUserProfilePic ? (
                            <img
                              src={otherUserProfilePic}
                              alt={getChatName(chat)}
                              className="w-10 h-10 rounded-full object-cover border-2 pointer-events-none"
                              style={{ borderColor: otherUserCircleColor || '#475569' }}
                            />
                          ) : (
                            <div 
                              className="w-10 h-10 rounded-full bg-slate-600 flex items-center justify-center text-white font-semibold border-2 pointer-events-none"
                              style={{ borderColor: otherUserCircleColor || '#475569' }}
                            >
                              {getChatName(chat).charAt(0).toUpperCase()}
                            </div>
                          )}
                          {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                              {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`font-semibold truncate ${otherUserBanner ? 'text-white drop-shadow-lg' : 'text-white'}`}>{getChatName(chat)}</div>
                          {chat.last_message && (
                            <div className={`text-sm truncate ${unreadCount > 0 ? 'text-white font-semibold' : otherUserBanner ? 'text-white/80 drop-shadow' : 'text-slate-400'}`}>
                              {chat.last_message}
                            </div>
                          )}
                        </div>
                      </div>
                      <span className={`digital-font font-semibold text-sm ${otherUserBanner ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                        {otherUserBattlePoints} BP
                      </span>
                    </div>
                    
                    {/* Trophy System - Top Right */}
                    <div className="absolute top-1 right-2 flex items-center gap-1 z-10">
                      <div className="flex items-center gap-0.5">
                        <Trophy className="w-3 h-3 text-yellow-500" />
                        <span className="text-[10px] font-bold text-white drop-shadow">{otherUserProfile.gold_trophies || 0}</span>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <Trophy className="w-3 h-3 text-slate-300" />
                        <span className="text-[10px] font-bold text-white drop-shadow">{otherUserProfile.silver_trophies || 0}</span>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <Trophy className="w-3 h-3 text-amber-600" />
                        <span className="text-[10px] font-bold text-white drop-shadow">{otherUserProfile.bronze_trophies || 0}</span>
                      </div>
                    </div>
                    
                    {/* EXP Progress Bar */}
                    <ExpProgressBar level={otherUserProfile.level || 1} exp={otherUserProfile.exp || 0} />
                  </button>
                );
              })}

              {/* Group Chats */}
              {chats.group_chats.map((chat) => {
                const unreadCount = chat.unread_counts?.[user.id] || 0;
                // Get creator's profile for group banner/picture
                const creatorProfile = chat.member_profiles?.[chat.creator_id] || {};
                const creatorBanner = creatorProfile.banner_image;
                
                return (
                  <button
                    key={chat.id}
                    onClick={() => loadChat(chat)}
                    className={`w-full text-left p-3 rounded-lg mb-2 transition-colors relative ${
                      activeChat?.id === chat.id
                        ? 'bg-cyan-500/30'
                        : 'bg-slate-700/50 hover:bg-slate-700'
                    }`}
                    style={creatorBanner ? {
                      backgroundImage: `url(${creatorBanner})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat'
                    } : {}}
                  >
                    {/* Overlay for better text visibility when banner is set */}
                    {creatorBanner && (
                      <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                    )}
                    
                    <div className="flex items-center gap-3 relative z-10">
                      <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white relative border-2 border-slate-600">
                        <Users className="w-5 h-5" />
                        {unreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold truncate ${creatorBanner ? 'text-white drop-shadow-lg' : 'text-white'}`}>{chat.name}</div>
                        {chat.last_message && (
                          <div className={`text-sm truncate ${unreadCount > 0 ? 'text-white font-semibold' : creatorBanner ? 'text-white/80 drop-shadow' : 'text-slate-400'}`}>
                            {chat.last_message}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}

              {chats.direct_chats.length === 0 && chats.group_chats.length === 0 && (
                <div className="text-center text-slate-500 py-8">
                  {chatsLoading ? 'Loading Chats…' : 'No chats yet. Start a conversation!'}
                </div>
              )}
            </div>
          </div>

          {/* Messages Area */}
          <div 
            className={`bg-slate-800/50 rounded-t-lg border-t border-slate-700 flex flex-col relative min-h-0 ${activeChat ? 'col-span-3 md:col-span-2' : 'hidden md:block md:col-span-2'}`}
            style={user.message_area_background ? {
              backgroundImage: `url(${user.message_area_background})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            } : {}}
          >
            {/* Overlay for better visibility */}
            {user.message_area_background && (
              <div className="absolute inset-0 bg-black/40 rounded-t-lg"></div>
            )}

            {activeChat ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-slate-700 flex justify-between items-center relative z-10">
                  <div className="flex items-center gap-3">
                    {(() => {
                      const otherUserId = activeChat.participants?.find(id => id !== user.id);
                      const otherUserProfile = activeChat.participant_profiles?.[otherUserId] || {};
                      const otherUserBattlePoints = otherUserProfile.battle_points || 0;
                      
                      return (
                        <>
                          {/* Combined Profile Picture + Name in Leaderboard Style */}
                          <div 
                            className="leaderboard-entry relative p-4 rounded-lg overflow-hidden inline-flex items-center justify-between gap-3 min-w-[350px] bg-slate-800/30 border border-slate-700/50 mr-auto"
                            style={otherUserProfile.banner_image && !isGroupChat(activeChat) ? {
                              backgroundImage: `url(${otherUserProfile.banner_image})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center'
                            } : {}}
                          >
                            {/* Dark overlay for better text visibility */}
                            {otherUserProfile.banner_image && !isGroupChat(activeChat) && (
                              <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                            )}
                            
                            {/* Level Badge */}
                            {!isGroupChat(activeChat) && (
                              <LevelBadge level={otherUserProfile.level || 1} small={true} verified={otherUserProfile.has_made_first_purchase} />
                            )}
                            
                            {/* Left side: Profile Picture + Name */}
                            <div className="flex items-center gap-3 relative z-10">
                              {/* Profile Picture */}
                              <div>
                                {otherUserProfile.profile_picture && !isGroupChat(activeChat) ? (
                                  <img
                                    src={otherUserProfile.profile_picture}
                                    alt="Profile"
                                    className="w-10 h-10 rounded-full object-cover border-2 pointer-events-none"
                                    style={{ borderColor: otherUserProfile.circle_color || '#475569' }}
                                  />
                                ) : !isGroupChat(activeChat) ? (
                                  <div 
                                    className="w-10 h-10 rounded-full bg-slate-600 border-2 flex items-center justify-center text-white font-semibold pointer-events-none"
                                    style={{ borderColor: otherUserProfile.circle_color || '#475569' }}
                                  >
                                    {getChatName(activeChat).charAt(0).toUpperCase()}
                                  </div>
                                ) : null}
                              </div>
                              
                              {/* Name */}
                              <h3 className={`text-lg font-semibold ${otherUserProfile.banner_image && !isGroupChat(activeChat) ? 'text-white drop-shadow-lg' : 'text-white'}`}>
                                {getChatName(activeChat)}
                              </h3>
                            </div>
                            
                            {/* Right side: Battle Points */}
                            {!isGroupChat(activeChat) && (
                              <span className={`digital-font font-semibold relative z-10 ${otherUserProfile.banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                                {otherUserBattlePoints} BP
                              </span>
                            )}
                            
                            {/* Trophy System - Top Right */}
                            {!isGroupChat(activeChat) && (
                              <div className="absolute top-1 right-2 flex items-center gap-1 z-10">
                                <div className="flex items-center gap-0.5">
                                  <Trophy className="w-3 h-3 text-yellow-500" />
                                  <span className="text-[10px] font-bold text-white drop-shadow">{otherUserProfile.gold_trophies || 0}</span>
                                </div>
                                <div className="flex items-center gap-0.5">
                                  <Trophy className="w-3 h-3 text-slate-300" />
                                  <span className="text-[10px] font-bold text-white drop-shadow">{otherUserProfile.silver_trophies || 0}</span>
                                </div>
                                <div className="flex items-center gap-0.5">
                                  <Trophy className="w-3 h-3 text-amber-600" />
                                  <span className="text-[10px] font-bold text-white drop-shadow">{otherUserProfile.bronze_trophies || 0}</span>
                                </div>
                              </div>
                            )}
                            
                            {/* EXP Progress Bar */}
                            {!isGroupChat(activeChat) && (
                              <ExpProgressBar level={otherUserProfile.level || 1} exp={otherUserProfile.exp || 0} />
                            )}
                          </div>
                        </>
                      );
                    })()}
                    {isGroupChat(activeChat) && (
                      <button 
                        onClick={fetchGroupMembers}
                        className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors cursor-pointer"
                      >
                        {activeChat.member_usernames.length} members
                      </button>
                    )}
                  </div>
                  
                  <div className="flex flex-col items-center gap-1.5 ml-2">
                    <button
                      onClick={() => setShowMessageAreaBgModal(true)}
                      className="text-cyan-400 hover:text-cyan-300 text-xs transition-colors"
                    >
                      Customize
                    </button>
                    
                    {isGroupChat(activeChat) && activeChat.creator_id === user.id ? (
                      <button
                        onClick={closeGroup}
                        className="text-red-400 hover:text-red-300 transition-colors"
                        title="Close Group"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    ) : !isGroupChat(activeChat) && (
                      <button
                        onClick={clearDirectChat}
                        className="text-red-400 hover:text-red-300 transition-colors"
                        title="Clear Chat"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Messages */}
                <div 
                  className="flex-1 overflow-y-auto p-4 space-y-3 relative z-10"
                  onClick={() => {
                    // Toggle focus/blur on tap
                    if (isInputFocused) {
                      messageInputRef.current?.blur();
                      setIsInputFocused(false);
                    } else {
                      messageInputRef.current?.focus();
                      setIsInputFocused(true);
                    }
                  }}
                >
                  {messages.map((msg) => {
                    const isOwnMessage = msg.sender_id === user.id;
                    const senderColor = msg.sender_color || '#3B82F6';
                    
                    // Get other user profile for direct chats
                    const otherUserId = activeChat.participants?.find(id => id !== user.id);
                    const otherUserProfile = activeChat.participant_profiles?.[otherUserId] || {};
                    
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'} ${isOwnMessage ? 'justify-start' : 'justify-start'} mb-1 gap-2`}
                      >
                        {/* Profile Picture for messages in direct chats */}
                        {!msg.is_system_message && !isGroupChat(activeChat) && (
                          <div className="flex-shrink-0 self-end mb-1">
                            {isOwnMessage ? (
                              // User's own profile picture
                              user.profile_picture ? (
                                <img
                                  src={user.profile_picture}
                                  alt="Your Profile"
                                  className="w-10 h-10 rounded-full object-cover border-2 pointer-events-none"
                                  style={{ borderColor: user.circle_color || '#475569' }}
                                />
                              ) : (
                                <div 
                                  className="w-10 h-10 rounded-full bg-slate-600 border-2 flex items-center justify-center text-white font-semibold text-sm pointer-events-none"
                                  style={{ borderColor: user.circle_color || '#475569' }}
                                >
                                  {user.username?.charAt(0).toUpperCase() || '?'}
                                </div>
                              )
                            ) : (
                              // Other user's profile picture
                              otherUserProfile.profile_picture ? (
                                <img
                                  src={otherUserProfile.profile_picture}
                                  alt="Profile"
                                  className="w-10 h-10 rounded-full object-cover border-2 pointer-events-none"
                                  style={{ borderColor: otherUserProfile.circle_color || '#475569' }}
                                />
                              ) : (
                                <div 
                                  className="w-10 h-10 rounded-full bg-slate-600 border-2 flex items-center justify-center text-white font-semibold text-sm pointer-events-none"
                                  style={{ borderColor: otherUserProfile.circle_color || '#475569' }}
                                >
                                  {msg.sender_username?.charAt(0).toUpperCase() || '?'}
                                </div>
                              )
                            )}
                          </div>
                        )}
                        
                        <div className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'} max-w-[70%] ${msg.is_system_message ? 'w-full max-w-full' : ''}`}>
                          {!msg.is_system_message && !isOwnMessage && isGroupChat(activeChat) && (
                            <div className="text-xs mb-1 font-semibold ml-3" style={{ color: senderColor }}>
                              {msg.sender_username}
                            </div>
                          )}
                          
                          <div
                            className={`relative px-3 py-2 overflow-hidden select-none ${
                              msg.is_system_message
                                ? 'bg-slate-700/50 text-slate-300 text-center text-sm italic rounded-full'
                                : isOwnMessage
                                ? 'rounded-[20px] rounded-tr-sm text-white'
                                : 'rounded-[20px] rounded-tl-sm text-white'
                            }`}
                            style={{
                              WebkitTouchCallout: 'none',
                              WebkitUserSelect: 'none',
                              ...(
                                !msg.is_system_message
                                  ? isOwnMessage 
                                    ? { backgroundColor: user.banner_color || '#3B82F6' }
                                    : { backgroundColor: senderColor }
                                  : {}
                              )
                            }}
                            onTouchStart={(e) => handleLongPressStart(msg, e)}
                            onTouchEnd={handleLongPressEnd}
                            onMouseDown={(e) => handleLongPressStart(msg, e)}
                            onMouseUp={handleLongPressEnd}
                            onMouseLeave={handleLongPressEnd}
                            onContextMenu={(e) => {
                              // Prevent context menu on long press
                              if (msg.sender_id === user.id && !msg.is_system_message) {
                                e.preventDefault();
                              }
                            }}
                          >
                            <div className="relative z-10">
                              {msg.image && (
                                <img 
                                  src={msg.image} 
                                  alt="Shared image" 
                                  className="max-w-[300px] max-h-[300px] rounded-lg mb-2 cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => window.open(msg.image, '_blank')}
                                />
                              )}
                              {msg.video && (
                                <video 
                                  controls 
                                  className="max-w-[400px] max-h-[300px] rounded-lg mb-2"
                                  preload="metadata"
                                >
                                  <source src={msg.video} type="video/mp4" />
                                  Your browser does not support video playback.
                                </video>
                              )}
                              {msg.content && (
                                <div className="break-words text-base font-medium">
                                  {msg.content}
                                  {msg.edited && (
                                    <span className="text-[8px] text-white/50 ml-2 italic">edit</span>
                                  )}
                                </div>
                              )}
                            </div>
                            {!msg.is_system_message && isGroupChat(activeChat) && activeChat.creator_id === user.id && !isOwnMessage && (
                              <button
                                onClick={() => kickMember(msg.sender_id)}
                                className="text-xs text-white/80 hover:text-white mt-2 flex items-center gap-1"
                              >
                                <UserMinus className="w-3 h-3" />
                                Kick
                              </button>
                            )}
                          </div>
                          
                          {/* Timestamp outside bubble */}
                          {!msg.is_system_message && (
                            <div className={`text-[10px] text-slate-400 mt-0.5 ${isOwnMessage ? 'text-right mr-2' : 'text-left ml-2'}`}>
                              {formatTimestamp(msg.timestamp)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div className="p-4 border-t border-slate-700 relative z-10">
                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="mb-2 relative inline-block">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="max-w-[200px] max-h-[200px] rounded-lg border-2 border-slate-600"
                      />
                      <button
                        onClick={clearImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  
                  {/* Video Preview */}
                  {videoPreview && (
                    <div className="mb-2 relative inline-block">
                      <video 
                        src={videoPreview} 
                        controls
                        className="max-w-[200px] max-h-[200px] rounded-lg border-2 border-slate-600"
                      />
                      <button
                        onClick={clearVideo}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  
                  {/* Converting Indicator */}
                  {isConverting && (
                    <div className="mb-2 text-cyan-400 text-sm animate-pulse">
                      🎬 Converting video to 1080p...
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                    {/* Hidden file inputs */}
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/*"
                      onChange={handleVideoSelect}
                      className="hidden"
                    />
                    <input
                      ref={videoCameraRef}
                      type="file"
                      accept="video/*"
                      capture="environment"
                      onChange={handleVideoSelect}
                      className="hidden"
                    />
                    
                    {/* Video button with dropdown */}
                    <div className="relative video-options-container">
                      <Button
                        onClick={() => setShowVideoOptions(!showVideoOptions)}
                        className="bg-slate-700 hover:bg-slate-600 relative z-10"
                        title="Attach video"
                        disabled={isConverting}
                      >
                        <Film className="w-4 h-4" />
                      </Button>
                      
                      {/* Video options dropdown */}
                      {showVideoOptions && (
                        <div className="absolute bottom-full mb-2 left-0 bg-slate-800 border border-slate-700 rounded-lg shadow-lg py-2 min-w-[180px] z-50">
                          <button
                            onClick={() => {
                              videoInputRef.current?.click();
                              setShowVideoOptions(false);
                            }}
                            className="w-full px-4 py-2 text-left text-white hover:bg-slate-700 flex items-center gap-2"
                          >
                            <Film className="w-4 h-4" />
                            Upload Video
                          </button>
                          <button
                            onClick={() => {
                              videoCameraRef.current?.click();
                              setShowVideoOptions(false);
                            }}
                            className="w-full px-4 py-2 text-left text-white hover:bg-slate-700 flex items-center gap-2"
                          >
                            <Film className="w-4 h-4" />
                            Record Video
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      className="bg-slate-700 hover:bg-slate-600 relative z-10"
                      title="Attach image"
                      disabled={isConverting}
                    >
                      <ImageIcon className="w-4 h-4" />
                    </Button>
                    <Input
                      ref={messageInputRef}
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      onFocus={() => setIsInputFocused(true)}
                      onBlur={() => setIsInputFocused(false)}
                      placeholder="Type a message..."
                      className="flex-1 bg-slate-700 border-slate-600 text-white relative z-10"
                      maxLength={1000}
                    />
                    <Button onClick={sendMessage} className="bg-cyan-500 hover:bg-cyan-600 relative z-10">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center relative z-10">
                <div className="p-4 border-b border-slate-700 flex justify-end relative z-10 w-full">
                  <button
                    onClick={() => setShowMessageAreaBgModal(true)}
                    className="text-cyan-400 hover:text-cyan-300 text-xs transition-colors"
                  >
                    Customize
                  </button>
                </div>
                <div className="flex-1 flex items-center justify-center">
                  <p className={`text-lg ${user.message_area_background ? 'text-white drop-shadow-lg' : 'text-slate-500'}`}>Select a chat to start messaging</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>

      {/* New Chat Modal */}
      <Dialog open={showNewChatModal} onOpenChange={setShowNewChatModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white top-[8%] translate-y-0 max-h-[75vh]">
          <DialogHeader>
            <DialogTitle className="text-cyan-400">New Chat</DialogTitle>
            <DialogDescription className="text-slate-400">Search for a blader to chat with</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-hidden flex flex-col max-h-[calc(70vh-8rem)]">
            <Input
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                searchUsers(e.target.value);
              }}
              placeholder="Search username..."
              className="bg-slate-800 border-slate-700 text-white flex-shrink-0"
            />
            <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
              {searchResults.map((result) => (
                <button
                  key={result.username}
                  onClick={() => createDirectChat(result.id || result.username, result.username)}
                  className="w-full leaderboard-entry bg-slate-800/30 border border-slate-700/50 rounded-lg p-3 hover:bg-slate-700 transition-colors flex justify-between items-center relative"
                  style={result.banner_image ? {
                    backgroundImage: `url(${result.banner_image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  } : {}}
                >
                  {/* Overlay for better text visibility when banner is set */}
                  {result.banner_image && (
                    <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                  )}
                  
                  {/* Level Badge */}
                  <LevelBadge level={result.level || 1} small={true} verified={result.has_made_first_purchase} />
                  
                  <div className="flex items-center gap-3 relative z-10 flex-1 min-w-0">
                    {/* Profile Picture */}
                    {result.profile_picture ? (
                      <img
                        src={result.profile_picture}
                        alt={result.username}
                        className="w-10 h-10 rounded-full object-cover border-2 flex-shrink-0 pointer-events-none"
                        style={{ borderColor: result.circle_color || '#475569' }}
                      />
                    ) : (
                      <div 
                        className="w-10 h-10 rounded-full bg-slate-600 border-2 flex items-center justify-center text-slate-400 font-semibold text-sm flex-shrink-0 pointer-events-none"
                        style={{ borderColor: result.circle_color || '#475569' }}
                      >
                        {result.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className={`font-semibold truncate ${result.banner_image ? 'text-white drop-shadow-lg' : 'text-white'}`}>
                      {result.username}
                    </span>
                  </div>
                  
                  <span className={`digital-font font-semibold text-sm relative z-10 ${result.banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                    {result.battle_points || 0} BP
                  </span>
                  
                  {/* Trophy System - Top Right */}
                  <div className="absolute top-1 right-2 flex items-center gap-1 z-10">
                    <div className="flex items-center gap-0.5">
                      <Trophy className="w-3 h-3 text-yellow-500" />
                      <span className="text-[10px] font-bold text-white drop-shadow">{result.gold_trophies || 0}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Trophy className="w-3 h-3 text-slate-300" />
                      <span className="text-[10px] font-bold text-white drop-shadow">{result.silver_trophies || 0}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Trophy className="w-3 h-3 text-amber-600" />
                      <span className="text-[10px] font-bold text-white drop-shadow">{result.bronze_trophies || 0}</span>
                    </div>
                  </div>
                  
                  {/* EXP Progress Bar */}
                  <ExpProgressBar level={result.level || 1} exp={result.exp || 0} />
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Group Modal */}
      <Dialog open={showNewGroupModal} onOpenChange={setShowNewGroupModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white top-[8%] translate-y-0 max-h-[75vh]">
          <DialogHeader>
            <DialogTitle className="text-cyan-400">Create Group</DialogTitle>
            <DialogDescription className="text-slate-400">Create a group chat with multiple bladers</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-hidden flex flex-col max-h-[calc(70vh-8rem)]">
            <Input
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Group name..."
              className="bg-slate-800 border-slate-700 text-white flex-shrink-0"
              maxLength={50}
            />
            <Input
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                searchUsers(e.target.value, true); // true = group mode, only show mutual friends
              }}
              placeholder="Search mutual friends to add..."
              className="bg-slate-800 border-slate-700 text-white flex-shrink-0"
            />
            
            {/* Selected Members */}
            {selectedMembers.length > 0 && (
              <div className="flex flex-wrap gap-2 flex-shrink-0">
                {selectedMembers.map((member) => (
                  <div
                    key={member.username}
                    className="bg-cyan-500/20 border border-cyan-500 rounded-full px-3 py-1 flex items-center gap-2"
                  >
                    <span className="text-sm">{member.username}</span>
                    <button
                      onClick={() => setSelectedMembers(prev => prev.filter(m => m.username !== member.username))}
                      className="text-cyan-400 hover:text-cyan-300"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Search Results */}
            <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
              {searchResults.filter(r => !selectedMembers.find(m => m.username === r.username)).map((result) => (
                <button
                  key={result.username}
                  onClick={() => setSelectedMembers(prev => [...prev, result])}
                  className="w-full p-3 bg-slate-800 hover:bg-slate-700 rounded-lg flex items-center gap-3 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center text-white text-sm font-semibold">
                    {result.username.charAt(0).toUpperCase()}
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-semibold">{result.username}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={createGroupChat}
              disabled={!groupName.trim() || selectedMembers.length === 0}
              className="bg-cyan-500 hover:bg-cyan-600"
            >
              Create Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chat List Background Modal */}
      <Dialog open={showChatListBgModal} onOpenChange={setShowChatListBgModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white top-[10%] translate-y-0">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 digital-font text-xl">CHAT LIST BACKGROUND</DialogTitle>
            <DialogDescription className="text-slate-400">
              Customize the chat list area background
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {user.chat_list_background && (
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Current Background</label>
                <div className="relative w-full h-40 rounded-lg overflow-hidden border-2 border-slate-700">
                  <img
                    src={user.chat_list_background}
                    alt="Chat list background"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="text-sm text-slate-400 mb-2 block">
                {user.chat_list_background ? 'Change Background' : 'Upload Background'}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleChatListBgUpload}
                disabled={uploadingChatListBg}
                className="block w-full text-sm text-slate-400
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-cyan-500 file:text-white
                  hover:file:bg-cyan-600
                  file:cursor-pointer cursor-pointer
                  disabled:opacity-50"
              />
              <p className="text-xs text-slate-500 mt-2">
                Recommended: JPG, PNG, or WEBP. Max size: 5MB
              </p>
            </div>

            {user.chat_list_background && (
              <Button
                onClick={handleRemoveChatListBg}
                variant="outline"
                className="w-full border-red-500 text-red-400 hover:bg-red-500/10"
              >
                Remove Background
              </Button>
            )}

            {uploadingChatListBg && (
              <div className="text-center text-sm text-cyan-400 animate-pulse">
                Uploading...
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowChatListBgModal(false)}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message Area Background Modal */}
      <Dialog open={showMessageAreaBgModal} onOpenChange={setShowMessageAreaBgModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white top-[10%] translate-y-0">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 digital-font text-xl">MESSAGE AREA BACKGROUND</DialogTitle>
            <DialogDescription className="text-slate-400">
              Customize the message area background
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {user.message_area_background && (
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Current Background</label>
                <div className="relative w-full h-40 rounded-lg overflow-hidden border-2 border-slate-700">
                  <img
                    src={user.message_area_background}
                    alt="Message area background"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="text-sm text-slate-400 mb-2 block">
                {user.message_area_background ? 'Change Background' : 'Upload Background'}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleMessageAreaBgUpload}
                disabled={uploadingMessageAreaBg}
                className="block w-full text-sm text-slate-400
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-cyan-500 file:text-white
                  hover:file:bg-cyan-600
                  file:cursor-pointer cursor-pointer
                  disabled:opacity-50"
              />
              <p className="text-xs text-slate-500 mt-2">
                Recommended: JPG, PNG, or WEBP. Max size: 5MB
              </p>
            </div>

            {user.message_area_background && (
              <Button
                onClick={handleRemoveMessageAreaBg}
                variant="outline"
                className="w-full border-red-500 text-red-400 hover:bg-red-500/10"
              >
                Remove Background
              </Button>
            )}

            {uploadingMessageAreaBg && (
              <div className="text-center text-sm text-cyan-400 animate-pulse">
                Uploading...
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowMessageAreaBgModal(false)}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notification Audio */}
      <audio ref={notificationAudioRef} src="/message-notification.mp3" preload="auto" />

      {/* Notification Popup */}
      {notification && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-slide-down">
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-2xl p-4 min-w-[320px] max-w-md">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <MessageCircle className="w-8 h-8 text-cyan-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-white mb-1">{notification.sender}</div>
                <div className="text-sm text-slate-300 truncate">{notification.message}</div>
              </div>
              <button
                onClick={() => setNotification(null)}
                className="flex-shrink-0 text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Friends Modal */}
      <Dialog open={showFriendsModal} onOpenChange={(open) => {
        console.log('Friends modal onOpenChange called, open:', open);
        setShowFriendsModal(open);
        if (open) {
          console.log('Modal opening, fetching friends...');
          fetchFriends();
        } else {
          console.log('Modal closing');
        }
      }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md top-[8%] translate-y-0 max-h-[75vh]">
          <DialogHeader>
            <DialogTitle className="text-purple-400">Friends List</DialogTitle>
            <DialogDescription className="text-slate-400">
              Add users to your friends list. Only mutual friends can be added to group chats.
            </DialogDescription>
          </DialogHeader>
          
          {/* Search for new friends */}
          <div className="space-y-3">
            <Input
              value={friendSearchQuery}
              onChange={(e) => {
                setFriendSearchQuery(e.target.value);
                searchForFriends(e.target.value);
              }}
              placeholder="Search bladers to add..."
              className="bg-slate-800 border-slate-700 text-white"
            />
            
            {/* Search Results */}
            {friendSearchResults.length > 0 && (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                <p className="text-xs text-slate-400">Search Results:</p>
                {friendSearchResults.map((result) => (
                  <div
                    key={result.id}
                    className="leaderboard-entry bg-slate-800/30 border border-slate-700/50 rounded-lg p-3 flex justify-between items-center relative"
                    style={result.banner_image ? {
                      backgroundImage: `url(${result.banner_image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat'
                    } : {}}
                  >
                    {/* Overlay for better text visibility when banner is set */}
                    {result.banner_image && (
                      <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                    )}
                    
                    {/* Level Badge */}
                    <LevelBadge level={result.level || 1} small={true} verified={result.has_made_first_purchase} />
                    
                    <div className="flex items-center gap-3 relative z-10 flex-1 min-w-0">
                      {/* Profile Picture */}
                      {result.profile_picture ? (
                        <img
                          src={result.profile_picture}
                          alt={result.username}
                          className="w-10 h-10 rounded-full object-cover border-2 flex-shrink-0 pointer-events-none"
                          style={{ borderColor: result.circle_color || '#475569' }}
                        />
                      ) : (
                        <div 
                          className="w-10 h-10 rounded-full bg-slate-600 border-2 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 pointer-events-none"
                          style={{ borderColor: result.circle_color || '#475569' }}
                        >
                          {result.username.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <span className={`font-semibold truncate block ${result.banner_image ? 'text-white drop-shadow-lg' : 'text-white'}`}>
                          {result.username}
                        </span>
                      </div>
                    </div>
                    
                    <span className={`digital-font font-semibold text-sm relative z-10 mr-2 ${result.banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                      {result.battle_points || 0} BP
                    </span>
                    
                    <button
                      onClick={() => addFriend(result.id)}
                      className="relative z-10 bg-green-500 hover:bg-green-600 text-white rounded-full p-2 transition-colors"
                      title="Add friend"
                    >
                      <UserPlus className="w-5 h-5" />
                    </button>
                    
                    {/* Trophy System - Top Right */}
                    <div className="absolute top-1 right-2 flex items-center gap-1 z-10">
                      <div className="flex items-center gap-0.5">
                        <Trophy className="w-3 h-3 text-yellow-500" />
                        <span className="text-[10px] font-bold text-white drop-shadow">{result.gold_trophies || 0}</span>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <Trophy className="w-3 h-3 text-slate-300" />
                        <span className="text-[10px] font-bold text-white drop-shadow">{result.silver_trophies || 0}</span>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <Trophy className="w-3 h-3 text-amber-600" />
                        <span className="text-[10px] font-bold text-white drop-shadow">{result.bronze_trophies || 0}</span>
                      </div>
                    </div>
                    
                    {/* EXP Progress Bar */}
                    <ExpProgressBar level={result.level || 1} exp={result.exp || 0} />
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Friends List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            <p className="text-sm text-slate-300 font-semibold">{friends.length} Friends {console.log('Rendering friends list, count:', friends.length, 'friends:', friends)}</p>
            {friends.length === 0 ? (
              <p className="text-slate-400 text-sm text-center py-4">No friends yet. Search above to add friends!</p>
            ) : (
              friends.map((friend) => (
                <div
                  key={friend.id}
                  className="leaderboard-entry bg-slate-800/30 border border-slate-700/50 rounded-lg p-3 flex justify-between items-center relative"
                  style={friend.banner_image ? {
                    backgroundImage: `url(${friend.banner_image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  } : {}}
                >
                  {/* Overlay for better text visibility when banner is set */}
                  {friend.banner_image && (
                    <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                  )}
                  
                  {/* Level Badge */}
                  <LevelBadge level={friend.level || 1} small={true} verified={friend.has_made_first_purchase} />
                  
                  <div className="flex items-center gap-3 relative z-10 flex-1 min-w-0">
                    {/* Profile Picture */}
                    {friend.profile_picture ? (
                      <img
                        src={friend.profile_picture}
                        alt={friend.username}
                        className="w-10 h-10 rounded-full object-cover border-2 flex-shrink-0"
                        style={{ borderColor: friend.circle_color || '#475569' }}
                      />
                    ) : (
                      <div 
                        className="w-10 h-10 rounded-full bg-slate-600 border-2 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
                        style={{ borderColor: friend.circle_color || '#475569' }}
                      >
                        {friend.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <span className={`font-semibold truncate block ${friend.banner_image ? 'text-white drop-shadow-lg' : 'text-white'}`}>
                        {friend.username}
                      </span>
                    </div>
                  </div>
                  
                  <span className={`digital-font font-semibold text-sm relative z-10 mr-2 ${friend.banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                    {friend.battle_points || 0} BP
                  </span>
                  
                  <button
                    onClick={() => {
                      if (window.confirm(`Remove ${friend.username} from friends?`)) {
                        removeFriend(friend.id);
                      }
                    }}
                    className="relative z-10 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 transition-colors"
                    title="Remove friend"
                  >
                    <UserMinus className="w-5 h-5" />
                  </button>
                  
                  {/* Trophy System - Top Right */}
                  <div className="absolute top-1 right-2 flex items-center gap-1 z-10">
                    <div className="flex items-center gap-0.5">
                      <Trophy className="w-3 h-3 text-yellow-500" />
                      <span className="text-[10px] font-bold text-white drop-shadow">{friend.gold_trophies || 0}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Trophy className="w-3 h-3 text-slate-300" />
                      <span className="text-[10px] font-bold text-white drop-shadow">{friend.silver_trophies || 0}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Trophy className="w-3 h-3 text-amber-600" />
                      <span className="text-[10px] font-bold text-white drop-shadow">{friend.bronze_trophies || 0}</span>
                    </div>
                  </div>
                  
                  {/* EXP Progress Bar */}
                  <ExpProgressBar level={friend.level || 1} exp={friend.exp || 0} />
                </div>
              ))
            )}
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => setShowFriendsModal(false)}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Members Modal */}
      <Dialog open={showMembersModal} onOpenChange={setShowMembersModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-cyan-400">Group Members</DialogTitle>
            <DialogDescription className="text-slate-400">
              {activeChat && isGroupChat(activeChat) && `${activeChat.member_usernames.length} members in ${getChatName(activeChat)}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {groupMembers.map((member) => (
              <div
                key={member.id}
                className="leaderboard-entry bg-slate-800/30 border border-slate-700/50 rounded-lg p-4 flex justify-between items-center relative"
                style={member.banner_image ? {
                  backgroundImage: `url(${member.banner_image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                } : {}}
              >
                {/* Overlay for better text visibility when banner is set */}
                {member.banner_image && (
                  <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                )}
                
                <div className="flex items-center gap-3 relative z-10">
                  {/* Profile Picture */}
                  {member.profile_picture ? (
                    <img
                      src={member.profile_picture}
                      alt={member.username}
                      className="w-10 h-10 rounded-full object-cover border-2 border-slate-600 flex-shrink-0 pointer-events-none"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-slate-600 border-2 border-slate-600 flex items-center justify-center text-slate-400 font-semibold text-sm flex-shrink-0 pointer-events-none">
                      {member.username.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <span className={`font-medium ${member.banner_image ? 'text-white drop-shadow-lg' : 'text-slate-200'}`}>
                      {member.username}
                    </span>
                    {activeChat && member.id === activeChat.creator_id && (
                      <span className="ml-2 text-xs text-yellow-400">(Host)</span>
                    )}
                  </div>
                </div>
                
                {/* Kick button - only show for host and not for themselves */}
                {activeChat && activeChat.creator_id === user.id && member.id !== user.id && (
                  <button
                    onClick={() => {
                      if (window.confirm(`Remove ${member.username} from the group?`)) {
                        kickMember(member.id);
                      }
                    }}
                    className="relative z-10 text-red-400 hover:text-red-300 transition-colors"
                    title="Remove member"
                  >
                    <UserMinus className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowMembersModal(false)}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message Action Menu */}
      <Dialog open={showMessageMenu} onOpenChange={setShowMessageMenu}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-cyan-400">Message Actions</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Button
              onClick={() => {
                setEditingMessage(selectedMessageForAction);
                setEditContent(selectedMessageForAction?.content || '');
                setShowMessageMenu(false);
              }}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
            >
              Edit Message
            </Button>
            <Button
              onClick={() => handleDeleteMessage(selectedMessageForAction?.id)}
              variant="outline"
              className="w-full border-red-500 text-red-400 hover:bg-red-500/10"
            >
              Delete Message
            </Button>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setShowMessageMenu(false);
                setSelectedMessageForAction(null);
              }}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Message Modal */}
      <Dialog open={!!editingMessage} onOpenChange={(open) => !open && setEditingMessage(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-cyan-400">Edit Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full p-3 bg-slate-800 border border-slate-700 rounded-lg text-white resize-none"
              rows={4}
              placeholder="Edit your message..."
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setEditingMessage(null);
                setEditContent('');
              }}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleEditMessage(editingMessage.id, editContent)}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              disabled={!editContent.trim()}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messages;
